/**
 * One registry for every status enum shown in the UI. Rendering goes through
 * `<StatusPill>` (components/status-pill.tsx); label/tone lookups go through
 * `statusMeta`. Never map a status to a label or color inline in a route.
 */

import {
  APPLICATION_STATUS_LABEL,
  type ApplicationStatus,
} from "./application-status"

export type StatusTone = "positive" | "neutral" | "danger" | "muted"

export type StatusDomain =
  | "job"
  | "hackathon"
  | "application"
  | "verification"
  | "payment"
  | "member"
  | "support"

export type StatusMeta = { label: string; tone: StatusTone }

const APPLICATION_TONES: Record<ApplicationStatus, StatusTone> = {
  applied: "neutral",
  reviewed: "neutral",
  shortlisted: "positive",
  rejected: "danger",
  withdrawn: "neutral",
}

const REGISTRY: Record<StatusDomain, Record<string, StatusMeta>> = {
  job: {
    open: { label: "Open", tone: "positive" },
    closed: { label: "Closed", tone: "muted" },
  },
  hackathon: {
    published: { label: "Live", tone: "positive" },
    draft: { label: "Draft", tone: "muted" },
    cancelled: { label: "Cancelled", tone: "danger" },
  },
  application: Object.fromEntries(
    (
      Object.entries(APPLICATION_STATUS_LABEL) as [ApplicationStatus, string][]
    ).map(([value, label]) => [
      value,
      { label, tone: APPLICATION_TONES[value] },
    ]),
  ),
  verification: {
    pending: { label: "Pending", tone: "neutral" },
    approved: { label: "Approved", tone: "positive" },
    rejected: { label: "Rejected", tone: "danger" },
  },
  payment: {
    created: { label: "Created", tone: "neutral" },
    paid: { label: "Paid", tone: "positive" },
    failed: { label: "Failed", tone: "danger" },
  },
  member: {
    invited: { label: "Invited", tone: "neutral" },
    active: { label: "Active", tone: "positive" },
    removed: { label: "Removed", tone: "muted" },
  },
  support: {
    open: { label: "Open", tone: "neutral" },
    in_progress: { label: "In progress", tone: "neutral" },
    resolved: { label: "Resolved", tone: "positive" },
    closed: { label: "Closed", tone: "muted" },
  },
}

export function statusMeta(domain: StatusDomain, status: string): StatusMeta {
  return (
    REGISTRY[domain][status] ?? {
      label: status.charAt(0).toUpperCase() + status.slice(1),
      tone: "neutral",
    }
  )
}

export function statusLabel(domain: StatusDomain, status: string): string {
  return statusMeta(domain, status).label
}
